import { computed, inject, Injectable, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { dsDtos } from '@vectronic/openApi-customer';
import { EMPTY, startWith, Subject, switchMap } from 'rxjs';
import { AnimalSpeciesRestService } from './animal-species-rest.service';

interface AnimalSpeciesState {
  animalSpecies: dsDtos.AnimalSpecies[];
}

@Injectable({ providedIn: 'root' })
export class AnimalSpeciesService {
  private animalSpeciesRestService = inject(AnimalSpeciesRestService);

  // state
  private state = signal<AnimalSpeciesState>({ animalSpecies: [] });

  // selectors
  animalSpecies = computed(() =>
    this.state().animalSpecies.filter(
      (species) => !species.isDeprecated && species.english !== 'Other',
    ),
  );
  otherSpecies = computed(() =>
    this.state().animalSpecies.find((species) => species.english === 'Other'),
  );
  allSpecies = computed(() => this.state().animalSpecies);

  isMandatoryProtectiveBeltSpecies(id: string | null | undefined): boolean {
    if (!id || id === 'Other') {
      return false;
    }
    return this.allSpecies().some(
      (species) =>
        species.id === id &&
        species.animalFeatureTypes?.includes(
          dsDtos.AnimalSpecies.AnimalFeatureTypesEnum.MandatoryProtectiveBelt,
        ),
    );
  }

  // sources
  reload$ = new Subject<void>();
  private speciesLoaded$ = this.animalSpeciesRestService.fetchAnimalSpecies();

  constructor() {
    // reducers
    this.reload$
      .pipe(
        takeUntilDestroyed(),
        startWith(EMPTY),
        switchMap(() => this.speciesLoaded$),
      )
      .subscribe((species) =>
        this.state.update((state) => ({
          ...state,
          animalSpecies: species,
        })),
      );
  }
}
